import { computed, inject, Injectable, resource, Signal } from '@angular/core';
import { TenantService } from '@cmi/addin-core';
import { DossierDetail } from '../dossiers/dossier.models';
import { DossierService } from '../dossiers/dossier.service';

/** The dossiers mails went into, by guid; null for one that could not be loaded. */
export interface FiledDossiers {
  /** Undefined while loading. */
  dossiers: Signal<ReadonlyMap<string, DossierDetail | null> | undefined>;
  loading: Signal<boolean>;
  reload(): void;
}

/** Looks up the dossiers a confirmation names, which a filed target holds by id only. */
@Injectable({
  providedIn: 'root'
})
export class FiledDossiersService {
  private readonly _dossierService = inject(DossierService);
  private readonly _tenantService = inject(TenantService);

  /**
   * Each dossier once, however often the mail went into it, and again when
   * the guids or the tenant change. A dossier that fails to load is null
   * rather than failing the rest: a dossier deleted since, or one on another
   * tenant, must not hide the others.
   *
   * Call it in the caller's injection context: the lookup ends with the caller.
   */
  public lookUp(guids: () => string[]): FiledDossiers {
    const dossiers = resource<
      ReadonlyMap<string, DossierDetail | null>,
      { guids: string[]; tenant: string | null }
    >({
      params: () => ({
        guids: [...new Set(guids())],
        tenant: this._tenantService.selectedTenantName()
      }),
      loader: async ({ params }) =>
        new Map(
          await Promise.all(
            params.guids.map(async (guid) => [guid, await this._loadDossier(guid)] as const)
          )
        )
    });

    return {
      dossiers: computed(() => (dossiers.hasValue() ? dossiers.value() : undefined)),
      loading: computed(() => dossiers.isLoading()),
      reload: (): void => void dossiers.reload()
    };
  }

  private async _loadDossier(guid: string): Promise<DossierDetail | null> {
    try {
      return await this._dossierService.getDossier(guid);
    } catch (error) {
      console.error(`Loading the dossier ${guid} the mail was filed into failed:`, error);
      return null;
    }
  }
}
